import React, { useEffect, useState } from 'react';

import styles from 'pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptOneTermOffer/AcceptOneTermOffer.module.scss';
import { LoadingType } from 'actions';
import { clearApplication } from 'actions/application';
import { setDashboardStep } from 'actions/stages';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import Loader from 'components/Loader/index';
import { Lender } from 'enums/ApplicationsVariables';
import { DashboardStep } from 'enums/DashboardStep';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';
import { QUILO_FIXED_RATE_DESCRIPTION } from 'pages/Dashboard/steps/AcceptOffer/acceptOffer.constants';
import { Text } from 'pages/Dashboard/steps/AcceptOffer/acceptOffer.enums';
import DeclineOfferThanksPopup from 'pages/Dashboard/steps/AcceptOffer/common/DeclineOfferThanksPopup';
import LoanOfferCard from 'pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptOneTermOffer/LoanOfferCard/LoanOfferCard';
import LoanOfferOptions from 'pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptOneTermOffer/LoanOfferOptions/LoanOfferOptions';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { OfferDto, offersApi } from 'api/OffersApi';
import CreditServicesAgreementPopup from 'pages/Dashboard/steps/AcceptOffer/common/CreditServicesAgreementPopup/CreditServicesAgreementPopup';
import DashboardFormHeader from 'components/DashboardFormHeader';
import { ProgressBarStatus } from 'enums/ProgressBarStatus';
//TODO move to Offer?

const AcceptOneTermOffer = () => {
  const { variables, applicationId } = useAppSelector((state) => state.application);
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const dispatch = useAppDispatch();
  const [isDeclineOfferThanksPopupOpen, setIsDeclineOfferThanksPopupOpen] = useState(false);
  const [loanOffers, setLoanOffers] = useState<OfferDto[]>([]);
  const [isAnyLenderQuilo, setIsAnyLenderQuilo] = useState<boolean>(false);
  const [isCreditServiceAgreementPopupOpen, setIsCreditServiceAgreementPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getLoans = async () => {
      try {
        setIsLoading(true);

        const offersResponse = await offersApi.getOneTermOffers(applicationId!);

        setLoanOffers(offersResponse);
        setIsAnyLenderQuilo(offersResponse.map((offer) => offer.lender).includes(Lender.Quilo));
      } catch (error) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        setIsLoading(false);
      }
    };

    getLoans();
  }, [applicationId]);

  const handleAcceptOffer = async () => {
    setIsLoading(true);

    try {
      await offersApi.acceptOneTermOffer(applicationId!);

      const loanOfferWithUrl = loanOffers.find((offer) => offer.offerUrl);

      if (loanOfferWithUrl) {
        window.open(loanOfferWithUrl.offerUrl, '_self');
      } else {
        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
        dispatch(setDashboardStep(null));
      }
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsLoading(true);
      await offersApi.declineOffer({ applicationId: applicationId!, isWithdrawn: false });

      setIsDeclineOfferThanksPopupOpen(true);
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePopup = async () => {
    setIsDeclineOfferThanksPopupOpen(false);
    dispatch(clearApplication());
    dispatch(setDashboardStep(DashboardStep.NoActiveApplication));
  };

  const handleCloseCreditServiceAgreementPopup = () => {
    setIsCreditServiceAgreementPopupOpen(false);
  };

  const handleOpenCreditServiceAgreementPopup = () => {
    setIsCreditServiceAgreementPopupOpen(true);
  };

  return (
    <>
      {isLoading || isGlobalLoading ? (
        <Loader />
      ) : (
        <>
          <DashboardFormHeader
            mainMessage="Congratulations! Your Loan Is Prequalified Subject To Review By The Lending Underwriter"
            percent={ProgressBarStatus.Done}
            isDefaultStyles
          />
          <div className={styles.acceptOfferContainer}>
            {loanOffers && (
              <FormBody formBodyClassName={styles.formBody}>
                <div className={styles.offerCards}>
                  {loanOffers.map((loanOffer, index) => {
                    return (
                      <LoanOfferCard
                        key={index}
                        title={loanOffers.length > 1 ? `Loan ${index + 1}` : ``}
                        loanOffer={loanOffer}
                        lender={variables!.lender!}
                      />
                    );
                  })}
                  <LoanOfferOptions />
                </div>
                {isAnyLenderQuilo && <div className={styles.fixedRateDescription}>{QUILO_FIXED_RATE_DESCRIPTION}</div>}
                <Button className={styles.acceptOfferButton} onClick={handleOpenCreditServiceAgreementPopup}>
                  {Text.Proceed}
                </Button>
                <span className={styles.declineOffer} onClick={handleDecline}>
                  {Text.Decline}
                </span>
              </FormBody>
            )}
          </div>
          {isCreditServiceAgreementPopupOpen && (
            <CreditServicesAgreementPopup
              isModalOpen={isCreditServiceAgreementPopupOpen}
              onClose={handleCloseCreditServiceAgreementPopup}
              onSubmit={handleAcceptOffer}
              lender={variables!.lender!}
            />
          )}
        </>
      )}
      <DeclineOfferThanksPopup isModalOpen={isDeclineOfferThanksPopupOpen} onClose={handleClosePopup} />
    </>
  );
};

export default AcceptOneTermOffer;
